import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Where "add documents" sends people, and what the control that sends them
 * there is called.
 *
 * One helper rather than several call sites. Brain → Sources, where files are
 * added. It was `/brain` while Sources was unbuilt, which opens Ask; it moved
 * here on 2026-10-03, when setup's last screen began to lead to it.
 */
export function brainPath(projectId: string): string {
  return `/projects/${projectId}/brain/sources`;
}

/** The label on every control that goes there. */
export const ADD_DOCUMENTS_LABEL = "Add documents to Brain";

/**
 * A `Link`, not a `button`: it navigates and nothing else.
 *
 * It has nothing to commit before it goes: a mutation still in flight carries
 * on regardless, since unmounting a component does not cancel one.
 */
export function AddDocumentsToBrain({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}) {
  return (
    <Button variant="secondary" size="sm" asChild className={className}>
      <Link to={brainPath(projectId)}>
        {ADD_DOCUMENTS_LABEL}
        {/* Decorative. The accessible name is the label alone — a screen reader
            announcing "right arrow" after it adds nothing. */}
        <span aria-hidden>→</span>
      </Link>
    </Button>
  );
}

/**
 * The offer above the project header card.
 *
 * Feature-local by design: `ComplianceBanner` is Today's and hardcodes
 * `ComplianceAlert`, so there is nothing reusable to reach for. The tint family
 * rather than `bg-chrome` because this is an offer, not an alert — the dark
 * head is the app's "something is wrong" motif and this is not that.
 */
export function AddDocumentsBanner({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "flex flex-wrap items-center justify-between gap-4 rounded-card-lg",
        "border-[1.5px] border-brand-border bg-brand-tint-2 px-6 py-[18px]",
        className,
      )}
    >
      <p className="text-[0.95rem] font-semibold text-ink">
        Want to add more documents to the project?
      </p>
      <AddDocumentsToBrain projectId={projectId} />
    </section>
  );
}
